import { HiOutlineRocketLaunch } from 'react-icons/hi2';
import { useEffect, useState } from 'react';
import { GoSync } from 'react-icons/go';
import { toastSuccess } from '../utils/toasts';
import InvitationDTO from '../models/invitation';
import { displayUserPermissions } from '../models/user';
import { useDeleteInvitationMutation } from '../store';
import FilterTable, { BaseData } from './Table/Table';
import EmptyTable from './Table/EmptyTable';
import Button from './atoms/Button';
import CustomModal from './atoms/CustomModal';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import LoadingLottie from './atoms/LoadingLottie';

interface Props {
  invitations: InvitationDTO[];
  customButton?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    variant: 'primary' | 'secondary' | 'tertiary';
  };
}

interface TableInvitation extends BaseData {
  id: string;
  email: JSX.Element;
  permissions: JSX.Element;
}

const InvitationsTable: React.FC<Props> = ({ invitations, customButton }) => {
  const [deleteInvitation, resDeleteInvitation] = useDeleteInvitationMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TableInvitation | null>(null);
  const { business, businessLoading } = useGetSelectedBusiness();

  useEffect(() => {
    if (resDeleteInvitation.isSuccess) {
      toastSuccess('Invitación eliminada correctamente');
    }
  }, [resDeleteInvitation.isSuccess]);

  const config = [
    {
      label: 'Correo',
      id: 'email',
    },
    {
      label: 'Permisos',
      id: 'permissions',
    },
  ];

  const handleDeleteInvitation = (row: TableInvitation) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (selectedRow) {
      deleteInvitation({ businessId: business?.id ?? '', invitationId: selectedRow.id });
      setIsModalOpen(false);
    }
  };

  const actions = (row: TableInvitation) => (
    <div className='flex flex-row gap-2 items-center justify-center'>
      {resDeleteInvitation.isLoading ? (
        <div className="animate-spin">
          <GoSync className="animate-spin" />
        </div>
      ) : (
        <img
          src='/trash.svg'
          alt="Eliminar"
          onClick={() => handleDeleteInvitation(row)}
          className="cursor-pointer"
        />
      )}
    </div>
  );

  const tableRows: TableInvitation[] = invitations.map((invitation) => ({
    id: invitation.id,
    email: <p className="whitespace-nowrap">{invitation.email}</p>,
    permissions: <p className="whitespace-nowrap">{displayUserPermissions[
      invitation.permissions as keyof typeof displayUserPermissions
    ]}</p>,
  }));

  if (businessLoading) {
    return <LoadingLottie />;
  }

  return (
    <div>
      {invitations.length > 0 && (
        <div>
          <FilterTable
            rows={tableRows}
            headCells={config}
            actions={actions}
            pagination={{ pageSize: 10, enabled: true }}
            searchEnabled
            tableId="invitations-table"
            customButtons={customButton ? [(
              <Button
                key="custom-button"
                onClick={customButton.onClick}
                variant={customButton.variant}
              >
                <div className='flex flex-row gap-2 items-center justify-center whitespace-nowrap'>
                  {customButton.icon && customButton.icon}
                  <p className='whitespace-nowrap hidden md:block'>{customButton.label}</p>
                </div>
              </Button>
            )] : []}
          />
        </div>
      )}
      {invitations.length === 0 && (
        <EmptyTable
          title="Aún no hay invitaciones pendientes"
          info="Por el momento no hay invitaciones pendientes"
          icon={HiOutlineRocketLaunch}
          iconColor="text-secondary-500"
          button={customButton ? [
            {
              label: customButton.label,
              onClick: customButton.onClick,
              variant: customButton.variant,
              reactIcon: customButton.icon,
            },
          ] : undefined}
        />
      )}
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        hideCancelButton
      >
        <div className='flex flex-col gap-4'>
          <h1>Eliminar invitación</h1>
          <p>¿Estás seguro que querés eliminar esta invitación?</p>
          <div className='flex flex-row gap-2'>
            <Button onClick={() => setIsModalOpen(false)} variant="secondary">Cancelar</Button>
            <Button onClick={confirmDelete}>Eliminar</Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default InvitationsTable;
